import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";

// Widest a page is drawn, in pixels: well under the canvas size iPhones refuse to draw.
const MAX_RENDER_WIDTH = 2000;

// Shows a PDF's pages, drawn with pdf.js. An <embed> or <iframe> needs the browser's own PDF
// viewer, which phones don't have: Chrome on Android shows a blank box and iPhones show one
// page. pdf.js is loaded only on pages that use this.
export default function PdfViewer({ src, title }) {
  const pagesRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | failed

  useEffect(() => {
    const container = pagesRef.current;
    let cancelled = false;
    let loadingTask = null;
    setStatus("loading");

    (async () => {
      // The legacy build also runs on older phone browsers (iOS Safari 16, older Chrome).
      const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
      if (cancelled) return;
      // Written this way so webpack bundles the worker itself, from the same pdf.js version.
      // (A plain URL to the worker file gets broken by the build.) One worker serves every PDF.
      if (!pdfjs.GlobalWorkerOptions.workerPort) {
        pdfjs.GlobalWorkerOptions.workerPort = new Worker(
          new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url)
        );
      }

      loadingTask = pdfjs.getDocument(src);
      const pdf = await loadingTask.promise;
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;

        // Drawn at twice the screen's resolution, so the small print stays sharp when a phone
        // user zooms in.
        const pageWidth = page.getViewport({ scale: 1 }).width;
        const renderWidth = Math.min(container.clientWidth * (window.devicePixelRatio || 1) * 2, MAX_RENDER_WIDTH);
        const viewport = page.getViewport({ scale: renderWidth / pageWidth });

        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.setAttribute("role", "img");
        canvas.setAttribute("aria-label", `${title}, page ${pageNumber} of ${pdf.numPages}`);
        Object.assign(canvas.style, {
          display: "block",
          width: "100%",
          height: "auto",
          marginBottom: "16px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.15)",
          background: "white",
        });
        await page.render({ canvas, viewport }).promise;
        if (cancelled) return;
        container.appendChild(canvas);
        setStatus("ready");
      }
    })().catch((err) => {
      if (cancelled) return;
      console.error(`Couldn't show ${src}`, err);
      setStatus("failed");
    });

    return () => {
      cancelled = true;
      loadingTask?.destroy();
      container.replaceChildren();
    };
  }, [src, title]);

  return (
    <Box sx={{ width: "100%", maxWidth: 932, mx: "auto", px: 2 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<DownloadIcon />} href={src} download>
          Download PDF
        </Button>
      </Stack>
      {status === "loading" && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress aria-label={`Loading ${title}`} />
        </Box>
      )}
      {status === "failed" && (
        <Alert severity="warning">
          We couldn't show the brochure here. Use Download PDF above to open it.
        </Alert>
      )}
      {/* pdf.js adds a canvas per page in here; React leaves it alone. */}
      <Box ref={pagesRef} />
    </Box>
  );
}
